import React, { useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useThreadQuery } from '@/features/internal/patient-messaging/hooks/queries/useThreadQuery'
import LineChatMessage from './LineChatMessage'
import LineChatInput from './LineChatInput'

interface LineChatProps {
  threadId: string
}

const LineChat: React.FC<LineChatProps> = ({ threadId }) => {
  const { data, isLoading, isError, refetch, isFetching } =
    useThreadQuery(threadId)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const recentMessages = data?.recent_messages

  // メッセージを古い順にソート
  const sortedMessages = useMemo(() => {
    if (!recentMessages) return []
    return [...recentMessages].sort(
      (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
    )
  }, [recentMessages])

  // 日付ヘッダー用にメッセージをグループ化
  const groupedMessages = useMemo(() => {
    const groups: { date: string; messages: typeof sortedMessages }[] = []
    let currentDate = ''

    for (const msg of sortedMessages) {
      const date = new Date(msg.sent_at).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      if (date !== currentDate) {
        currentDate = date
        groups.push({ date, messages: [] })
      }
      groups[groups.length - 1].messages.push(msg)
    }

    return groups
  }, [sortedMessages])

  // 新メッセージ追加時に最下部へスクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [sortedMessages.length])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          メッセージの取得に失敗しました
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* ヘッダー（リロードボタン） */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tooltip title="メッセージを更新">
          <IconButton
            onClick={() => refetch()}
            disabled={isFetching}
            size="small"
          >
            <RefreshIcon
              sx={{
                fontSize: 20,
                animation: isFetching ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {/* メッセージ一覧 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 1.5,
          py: 1,
        }}
      >
        {groupedMessages.map((group) => (
          <React.Fragment key={group.date}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: 'text.secondary',
                my: 1.5,
              }}
            >
              {group.date}
            </Typography>
            {group.messages.map((message) => (
              <LineChatMessage key={message.id} message={message} />
            ))}
          </React.Fragment>
        ))}
        {sortedMessages.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mt: 4,
            }}
          >
            まだメッセージはありません
          </Typography>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* 入力欄 */}
      <LineChatInput threadId={threadId} />
    </Box>
  )
}

export default LineChat
